import PageContainer from '@components/PageContainer';
import AuthFormLayout from '@layouts/form/AuthFormLayout';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useMixpanel } from '../../context/MixpanelProvider';
import { trackSanitizedException } from '../../observability/appInsights';
import { useRegister } from '../../hooks/postQueries';
import { useSession } from '../../hooks/useSession';
import { AUTH_EVENTS } from '../../utils/eventSchema';
import { setAccessToken } from '../../utils/authTokenStore';
import {
  getPasswordPolicyErrorMessage,
  PASSWORD_POLICY_HINT,
  validatePasswordPolicy,
} from '../../utils/passwordPolicy';
const CreateAccount = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email;
  const otp = location.state?.otp;

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { mutate: register, isPending } = useRegister();
  const { trackEvent, identifyUser, aliasUser } = useMixpanel();
  const { setIsAuthenticated } = useSession();
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      email: email,
      first_name: '',
      last_name: '',
      password: '',
      confirmPassword: '',
    },
  });

  const validateConfirmPassword = (value) => {
    if (value !== watch('password')) return 'Passwords must match';
    return true;
  };

  const onSubmit = async (data) => {
    const { ...submitData } = data;
    submitData.purpose = 'register_user';
    submitData.otp = typeof otp === 'string' ? otp : otp.join('');
    submitData.customer_name = 'string';
    submitData.customer_id = 'string';
    await register(submitData, {
      onSuccess: (res) => {
        setAccessToken(res.data.access_token);
        // Update session context immediately
        setIsAuthenticated(true);

        // Add Mixpanel tracking here
        const { id: user_id, email, is_onboarding } = res.data;
        // mixpanel.get_distinct_id() throws when Mixpanel was never initialized
        // (cookies rejected); tracking must not stop the redirect below.
        try {
          // Step 1: Get the anonymous ID that was being tracked
          const anonymousId = mixpanel.get_distinct_id();
          // Step 2: Link anonymous user to permanent user ID
          aliasUser(anonymousId, user_id);
          // Step 3: Identify the user with their permanent attributes
          identifyUser(user_id, {
            email: email,
            $email: email,
            first_name: data.first_name,
            last_name: data.last_name,
            created_at: new Date().toISOString(),
          });
          // Step 4: Track the signup event
          trackEvent('Auth', {
            ...AUTH_EVENTS.SIGNUP_COMPLETED,
            user_id,
            email: email,
            signup_method: 'email',
            created_at: new Date().toISOString(),
          });
        } catch (error) {
          console.warn('Signup analytics failed', error);
          trackSanitizedException(error, { source: 'signup_mixpanel_tracking' });
        }
        localStorage.setItem('isOnboarding', is_onboarding);
        navigate('/onboarding');
        toast.success('Account created successfully!');
      },
      onError: (error) => {
        toast.error(getPasswordPolicyErrorMessage(error, 'Failed to create account'));
      },
    });
  };

  useEffect(() => {
    if (!otp || !email) {
      navigate('/signup', { state: { email, otp } });
    }
  }, []);

  return (
    <PageContainer title="Qualz.ai | Signup">
      <AuthFormLayout title="Create account 👋" text="You're almost there! Create your account.">
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel htmlFor="first_name" sx={{ mb: 1, fontWeight: '500' }}>
              First Name
            </FormLabel>
            <Controller
              name="first_name"
              control={control}
              rules={{ required: 'First name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="First Name"
                  error={!!errors.first_name}
                  helperText={errors.first_name?.message}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel htmlFor="last_name" sx={{ mb: 1, fontWeight: '500' }}>
              Last Name
            </FormLabel>
            <Controller
              name="last_name"
              control={control}
              rules={{ required: 'Last name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Last Name"
                  error={!!errors.last_name}
                  helperText={errors.last_name?.message}
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <FormLabel htmlFor="password" sx={{ mb: 1, fontWeight: '500' }}>
              Create Password
            </FormLabel>
            <Controller
              name="password"
              control={control}
              rules={{ validate: validatePasswordPolicy }}
              render={({ field }) => {
                const error = errors.password;
                return (
                  <TextField
                    {...field}
                    fullWidth
                    autoComplete="new-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Create Password"
                    error={!!error}
                    helperText={error?.message || PASSWORD_POLICY_HINT}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              }}
            />
          </FormControl>

          <FormControl fullWidth sx={{ mb: 3 }}>
            <FormLabel htmlFor="confirmPassword" sx={{ mb: 1, fontWeight: '500' }}>
              Re-enter Password
            </FormLabel>
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: 'Please confirm your password',
                validate: validateConfirmPassword,
              }}
              render={({ field }) => {
                const error = errors.confirmPassword;
                return (
                  <TextField
                    {...field}
                    fullWidth
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Re-enter Password"
                    error={!!error}
                    helperText={error?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            edge="end"
                          >
                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                );
              }}
            />
          </FormControl>

          <Button fullWidth variant="contained" size="large" type="submit" disabled={isPending}>
            {isPending ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
          </Button>
        </form>
      </AuthFormLayout>
    </PageContainer>
  );
};

export default CreateAccount;
